import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertTriangle, Camera, FileText, MessageCircle, BookOpen, BarChart3, CreditCard, Layers } from 'lucide-react';
import AttachmentPreview from '@/components/AttachmentPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const phases = ['Lead', 'Consultation', 'Design', 'Execution', 'Handover'];

function formatDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}
function formatINR(n) {
  if (!n && n !== 0) return '0';
  return Number(n).toLocaleString('en-IN');
}

const snagColors = { Open: 'bg-[#C4727F]/10 text-[#C4727F]', 'In Progress': 'bg-[#C67D5B]/10 text-[#C67D5B]', Resolved: 'bg-[#7DA68A]/10 text-[#7DA68A]' };

// Convert a project file (drawing/photo/doc) into an AttachmentPreview-shaped object
function toAttachment(f) {
  if (!f) return null;
  return {
    id: f.id || f.name,
    name: f.name || f.caption || 'File',
    type: f.type || '',
    size: f.size || 0,
    data_url: f.data_url || f.url || ''
  };
}

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('progress');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/portal/${token}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Access denied');
        }
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center" data-testid="portal-loading">
      <div className="w-8 h-8 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center" data-testid="portal-error">
      <div className="text-center max-w-sm px-6">
        <div className="w-16 h-16 rounded-2xl bg-[#C4727F]/10 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={28} strokeWidth={1.5} className="text-[#C4727F]" />
        </div>
        <h2 className="text-lg font-['Manrope'] font-bold text-[#2C2C2E] mb-2">Access Denied</h2>
        <p className="text-sm text-[#6B7280]">{error}</p>
        <p className="text-xs text-[#6B7280] mt-4">If you believe this is an error, please contact Interior Story studio.</p>
      </div>
    </div>
  );

  const phaseIdx = phases.indexOf(data.phase);
  const billing = data.billing_summary;
  // brief may be an object (questionnaire) or a string; normalize for safe rendering
  const briefText = typeof data.brief === 'string'
    ? data.brief
    : (data.brief && typeof data.brief === 'object' && Object.keys(data.brief).length > 0
        ? Object.entries(data.brief)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join('\n')
        : '');

  const sections = [
    { id: 'progress', label: 'Progress', icon: CheckCircle },
    ...(briefText ? [{ id: 'brief', label: 'Brief', icon: BookOpen }] : []),
    { id: 'timeline', label: 'Timeline', icon: BarChart3, count: data.timeframe?.length || 0 },
    { id: 'meetings', label: 'Meetings', icon: MessageCircle, count: data.meetings?.length || 0 },
    { id: 'drawings', label: 'Drawings', icon: Layers, count: data.drawings?.length || 0 },
    { id: 'photos', label: 'Gallery', icon: Camera, count: data.photos?.length || 0 },
    { id: 'snags', label: 'Snag List', icon: AlertTriangle, count: data.snag_list?.length || 0 },
    ...(billing ? [{ id: 'billing', label: 'Billing', icon: CreditCard }] : []),
    { id: 'documents', label: 'Documents', icon: FileText, count: data.handover?.length || 0 },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="client-portal-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#3A8C8C] flex items-center justify-center">
              <span className="text-white text-sm font-bold font-['Manrope']">IS</span>
            </div>
            <span className="font-semibold text-[#2C2C2E] text-base font-['Manrope'] tracking-tight">Interior Story</span>
          </div>
          <h1 className="text-2xl font-['Manrope'] font-bold text-[#2C2C2E]" data-testid="portal-project-name">{data.project_name}</h1>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-[#6B7280] flex-wrap">
            <span>Client: {data.client_name}</span>
            <span>&middot;</span>
            <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[10px]">{data.project_type}</Badge>
            <span>&middot;</span>
            <span>Phase: <span className="font-medium text-[#2C2C2E]">{data.phase}</span></span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Phase progress */}
        <div className="glass-card rounded-2xl p-6" data-testid="portal-progress">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-heading text-sm font-semibold font-['Manrope']">Project Progress</h2>
            <span className="text-xs text-[#3A8C8C] font-medium">{data.completion}% complete</span>
          </div>
          <div className="flex gap-2 mb-3">
            {phases.map((p, i) => (
              <div key={p} className={`flex-1 h-2.5 rounded-full transition-colors ${i <= phaseIdx ? 'bg-[#3A8C8C]' : 'bg-[#E8E6E1]'}`} />
            ))}
          </div>
          <div className="flex justify-between">
            {phases.map((p, i) => (
              <span key={p} className={`text-[10px] ${i <= phaseIdx ? 'text-[#3A8C8C] font-medium' : 'text-[#6B7280]'}`}>{p}</span>
            ))}
          </div>
        </div>

        {/* Billing summary card — visible only when shared */}
        {billing && (
          <div className="grid grid-cols-3 gap-3" data-testid="portal-billing-summary">
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Billed</p>
              <p className="text-base font-bold text-[#2C2C2E] font-['Manrope'] mt-1">Rs {formatINR(billing.billed)}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Paid</p>
              <p className="text-base font-bold text-[#7DA68A] font-['Manrope'] mt-1" data-testid="portal-billing-paid">Rs {formatINR(billing.paid)}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Pending</p>
              <p className="text-base font-bold text-[#C67D5B] font-['Manrope'] mt-1" data-testid="portal-billing-pending">Rs {formatINR(billing.pending)}</p>
            </div>
          </div>
        )}

        {/* Section tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${activeSection === s.id ? 'bg-[#3A8C8C] text-white' : 'bg-white border border-[#E8E6E1] text-[#6B7280] hover:bg-[#FAFAF8]'}`}
              data-testid={`portal-tab-${s.id}`}>
              <s.icon size={14} strokeWidth={1.5} /> {s.label}
              {s.count !== undefined && s.count > 0 && (
                <Badge className={`text-[9px] h-4 px-1 ${activeSection === s.id ? 'bg-white/20 text-white' : 'bg-[#E8E6E1] text-[#2C2C2E]'}`}>{s.count}</Badge>
              )}
            </button>
          ))}
        </div>

        {/* Progress */}
        {activeSection === 'progress' && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Current Phase: {data.phase}</h3>
            <div className="space-y-3">
              {phases.map((p, i) => (
                <div key={p} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${i < phaseIdx ? 'bg-[#7DA68A]' : i === phaseIdx ? 'bg-[#3A8C8C]' : 'bg-[#E8E6E1]'}`}>
                    {i <= phaseIdx ? <CheckCircle size={14} strokeWidth={1.5} className="text-white" /> : <Clock size={14} strokeWidth={1.5} className="text-[#6B7280]" />}
                  </div>
                  <span className={`text-sm ${i <= phaseIdx ? 'text-[#2C2C2E] font-medium' : 'text-[#6B7280]'}`}>{p}</span>
                  {i < phaseIdx && <Badge className="bg-[#7DA68A]/10 text-[#7DA68A] text-[9px]">Completed</Badge>}
                  {i === phaseIdx && <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px]">In Progress</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Brief */}
        {activeSection === 'brief' && briefText && (
          <div className="glass-card rounded-2xl p-6" data-testid="portal-brief">
            <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-3">Project Brief</h3>
            <p className="text-sm text-[#2C2C2E] whitespace-pre-wrap leading-relaxed">{briefText}</p>
          </div>
        )}

        {/* Timeline */}
        {activeSection === 'timeline' && (
          <div className="space-y-3" data-testid="portal-timeline">
            {data.timeframe?.length > 0 ? data.timeframe.map((t) => (
              <div key={t.id || t.name} className="glass-card rounded-xl p-4">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-medium text-[#2C2C2E]">{t.name || t.title}</p>
                  <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px]">{t.status || 'Planned'}</Badge>
                </div>
                <p className="text-xs text-[#6B7280]">
                  {formatDate(t.start_date)} → {formatDate(t.end_date)}
                </p>
                {t.notes && <p className="text-xs text-[#6B7280] mt-1.5">{t.notes}</p>}
              </div>
            )) : <p className="text-sm text-[#6B7280] text-center py-8">No timeline milestones shared yet</p>}
          </div>
        )}

        {/* Meetings */}
        {activeSection === 'meetings' && (
          <div className="space-y-3" data-testid="portal-meetings">
            {data.meetings?.length > 0 ? data.meetings.map((m) => (
              <div key={m.id || m.date} className="glass-card rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[#3A8C8C]">{formatDate(m.date)}</span>
                  <span className="text-xs text-[#6B7280]">{m.location}</span>
                </div>
                <h4 className="text-sm font-medium text-[#2C2C2E]">{m.agenda}</h4>
                {m.decisions && <p className="text-xs text-[#6B7280] mt-2">Decisions: {m.decisions}</p>}
                {m.action_items && <p className="text-xs text-[#C67D5B] mt-1">Action Items: {m.action_items}</p>}
              </div>
            )) : <p className="text-sm text-[#6B7280] text-center py-8">No meetings shared</p>}
          </div>
        )}

        {/* Drawings — grouped by stage/folder (internal folders like CAD are excluded server-side) */}
        {activeSection === 'drawings' && (
          <div className="space-y-5" data-testid="portal-drawings">
            {data.drawings?.length > 0 ? Object.entries(
              data.drawings.reduce((acc, d) => { const s = d.stage || 'Drawings'; (acc[s] = acc[s] || []).push(d); return acc; }, {})
            ).map(([stage, items]) => (
              <div key={stage}>
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">{stage}</p>
                <div className="space-y-2">
                  {items.map((d) => (
                    <div key={d.id || d.name} className="w-full glass-card rounded-xl p-4 flex items-center gap-3" data-testid={`portal-drawing-${d.id || d.name}`}>
                      <FileText size={18} strokeWidth={1.5} className="text-[#3A8C8C] shrink-0" />
                      <button type="button" onClick={() => setPreview(toAttachment(d))} className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-[#2C2C2E] truncate">{d.name || 'Drawing'}{d.version > 1 ? ` (V${d.version})` : ''}</p>
                        <p className="text-xs text-[#6B7280]">{d.status || 'Shared'}{d.created_at ? ` · ${formatDate(d.created_at)}` : ''}</p>
                      </button>
                      <button type="button" onClick={() => setPreview(toAttachment(d))} className="text-[10px] text-[#3A8C8C] shrink-0">View →</button>
                      {d.can_download && (
                        <a href={d.data_url} download={d.name} className="text-[10px] text-[#3A8C8C] shrink-0 hover:underline" data-testid={`portal-drawing-download-${d.id || d.name}`}>Download</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )) : <p className="text-sm text-[#6B7280] text-center py-8">No drawings shared yet</p>}
          </div>
        )}

        {/* Photos */}
        {activeSection === 'photos' && (
          <div data-testid="portal-photos">
            {data.photos?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {data.photos.map((p) => (
                  <button key={p.id || p.url || p.data_url}
                    type="button"
                    onClick={() => setPreview(toAttachment(p))}
                    className="aspect-square rounded-xl bg-[#E8E6E1] overflow-hidden hover:ring-2 hover:ring-[#3A8C8C]/30 transition-all"
                    data-testid={`portal-photo-${p.id || p.name || 'item'}`}>
                    <img src={p.data_url || p.url} alt={p.caption || p.name || ''} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            ) : <p className="text-sm text-[#6B7280] text-center py-8">No photos shared yet</p>}
          </div>
        )}

        {/* Snag List */}
        {activeSection === 'snags' && (
          <div className="space-y-3" data-testid="portal-snags">
            {data.snag_list?.length > 0 ? data.snag_list.map((s) => (
              <div key={s.id || s.description} className="glass-card rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2C2C2E]">{s.description || s.location}</p>
                  <p className="text-xs text-[#6B7280] mt-0.5">{s.location} &middot; {formatDate(s.created_at)}</p>
                </div>
                <Badge className={`text-[10px] ${snagColors[s.status] || ''}`}>{s.status}</Badge>
              </div>
            )) : <p className="text-sm text-[#6B7280] text-center py-8">No snag items</p>}
          </div>
        )}

        {/* Billing */}
        {activeSection === 'billing' && billing && (
          <div className="glass-card rounded-2xl p-6 space-y-4" data-testid="portal-billing">
            <div>
              <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-2">Billing Summary</h3>
              <p className="text-xs text-[#6B7280]">{billing.entries_count} entries on record</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B7280]">Total Billed</span>
                <span className="font-medium text-[#2C2C2E]">Rs {formatINR(billing.billed)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B7280]">Paid</span>
                <span className="font-medium text-[#7DA68A]">Rs {formatINR(billing.paid)}</span>
              </div>
              <div className="flex items-center justify-between text-sm border-t border-[#E8E6E1] pt-3">
                <span className="text-[#6B7280]">Outstanding</span>
                <span className={`font-bold ${billing.pending > 0 ? 'text-[#C67D5B]' : 'text-[#7DA68A]'}`}>Rs {formatINR(billing.pending)}</span>
              </div>
            </div>
            <p className="text-[10px] text-[#6B7280] pt-2 border-t border-[#E8E6E1]">Detailed invoices are available with the studio team.</p>
          </div>
        )}

        {/* Documents */}
        {activeSection === 'documents' && (
          <div className="space-y-3" data-testid="portal-documents">
            {data.handover?.length > 0 ? data.handover.map((d) => (
              <button key={d.id || d.name} type="button"
                onClick={() => setPreview(toAttachment(d))}
                className="w-full glass-card rounded-xl p-4 flex items-center gap-3 hover:border-[#3A8C8C] transition-colors text-left"
                data-testid={`portal-document-${d.id || d.name}`}>
                <FileText size={18} strokeWidth={1.5} className="text-[#3A8C8C]" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#2C2C2E]">{d.name || d.description}</p>
                  <p className="text-xs text-[#6B7280]">{formatDate(d.created_at)}</p>
                </div>
                <Badge className="text-[10px] bg-[#E8E6E1] text-[#2C2C2E]">{d.status || 'Shared'}</Badge>
              </button>
            )) : <p className="text-sm text-[#6B7280] text-center py-8">No documents shared yet</p>}
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-8 border-t border-[#E8E6E1]">
          <p className="text-xs text-[#6B7280]">This portal is shared by Interior Story. Link expires on {formatDate(data.expires_at)}.</p>
          <p className="text-xs text-[#D4D0C8] mt-1">Interior Story &middot; Premium Interior Design Studio</p>
        </div>
      </div>

      {/* Inline attachment preview */}
      <AttachmentPreview attachment={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
